import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { DollarSign } from 'lucide-react';
import { Button } from '@roger/ui-components';
import { useStore } from '../../store';
import { PermissionSlug } from '../../constants/featureFlags';
import { getPatientBillingCycleCount } from '../../services/api';

/** Permission-gated link from a patient page back to Billing Manager,
 *  shown only when the patient has at least one billing cycle (any status). */
export const BillingManagerLink: React.FC<{ patientId: string }> = ({
  patientId,
}) => {
  const hasBillingAccess = useStore(state =>
    state.hasPermission(PermissionSlug.BILLING_ACCESS)
  );
  const hideImportedPatients = useStore(state => state.hideImportedPatients);
  const [count, setCount] = useState<number>(0);

  useEffect(() => {
    // Reset so a previous patient's count never lingers when patientId changes
    // or access is lost.
    setCount(0);
    if (!patientId || !hasBillingAccess) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await getPatientBillingCycleCount({
          query: {
            patient_id: patientId,
            hide_imported_patients: hideImportedPatients,
          },
        });
        if (!cancelled) setCount(res?.data?.count ?? 0);
      } catch (err) {
        console.error('Error fetching billing cycle count:', err);
        if (!cancelled) setCount(0);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [patientId, hasBillingAccess, hideImportedPatients]);

  if (!hasBillingAccess || count === 0) return null;

  return (
    <Button asChild type='button' variant='outline' size='sm'>
      <Link to='/billing' aria-label='View Claims'>
        <DollarSign className='mr-1 h-4 w-4' />
        View Claims
      </Link>
    </Button>
  );
};
